/**
 * LenisProvider — buttery editorial smooth-scroll on all public marketing
 * routes. Disabled on `/admin/*` so CRM scrolling stays native (long tables
 * + sticky toolbars feel wrong with momentum scroll).
 *
 * Honors prefers-reduced-motion.
 *
 * @example
 *   <BrowserRouter>
 *     <LenisProvider>
 *       <Routes>...</Routes>
 *     </LenisProvider>
 *   </BrowserRouter>
 */
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

function isAdminPath(pathname) {
  return pathname.startsWith("/admin");
}

export default function LenisProvider({ children }) {
  const location = useLocation();
  const lenisRef = useRef(null);
  const rafIdRef = useRef(null);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const destroy = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (lenisRef.current) {
        try { lenisRef.current.destroy(); } catch (_) { /* noop */ }
        lenisRef.current = null;
      }
      document.documentElement.classList.remove("lenis-active");
    };

    if (isAdminPath(location.pathname) || prefersReduced) {
      destroy();
      return destroy;
    }

    // Already mounted on a public route? Keep it — Lenis handles SPA navigation natively.
    if (lenisRef.current) return destroy;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false, // touch already feels native; smoothing it makes scroll laggy
    });
    lenisRef.current = lenis;
    document.documentElement.classList.add("lenis-active");

    const raf = (time) => {
      lenis.raf(time);
      rafIdRef.current = requestAnimationFrame(raf);
    };
    rafIdRef.current = requestAnimationFrame(raf);

    return destroy;
  }, [location.pathname]);

  return children;
}
